import React, { useEffect } from 'react';
import {
  AfterSubmitFunc,
  Button,
  ButtonColor,
  ButtonVariant,
  FormTemplate,
  IconType,
  SubmitButton
} from 'ui-builder';
import { AddSetupAreaRequest, TeamSetupArea, useTeamSetupAreasService } from 'nekst-api';
import { InformationMessage, usePopupFormManager } from 'features/nekst-widgets';
import { createPortal } from 'react-dom';
import { SetupAreaChooser } from 'features/tasks-forms';
import { useTeamSetupAreasLimitContextRequired } from './TeamSetupAreasLimitContext';
import NewMarketWidget from './NewMarketWidget';

export function CreateTeamSetupAreaForm(
  props: {
    afterSubmitFunc?: AfterSubmitFunc,
    withInformationMessage?: boolean,
  }
) {
  const teamSetupAreasService = useTeamSetupAreasService();

  return (
    <FormTemplate<AddSetupAreaRequest, TeamSetupArea>
      title="Add Market"
      submitFormFunc={async (data) => {
        return await teamSetupAreasService.addSetupArea(data);
      }}
      afterSubmitFunc={props.afterSubmitFunc}
      validationSchema={{
        setupAreaId: {
          type: 'int',
          constraints: {
            required: true,
          },
        },
      }}
      buttons={(
        <SubmitButton />
      )}
    >
      {props.withInformationMessage && (
        <InformationMessage>
          Choose the market in which you operate and we will pre-populate your account
          {' '}
          with the important Detail Boxes, Custom Workflows and train our AI to extract information
          {' '}
          from your uploaded contracts.
        </InformationMessage>
      )}
      <SetupAreaChooser
        requestNewMarketWidget={<NewMarketWidget />}
      />
    </FormTemplate>
  );
}

export function CreateTeamSetupAreaButtonPortal() {
  return (
    <span id="create-team-setup-area-button"></span>
  )
}

export function CreateTeamSetupAreaButton(props: {
  openFormOnLoad?: boolean,
}) {
  const popupFormManager = usePopupFormManager();

  const element = document.getElementById('create-team-setup-area-button');

  const [showButton, setShowButton] = React.useState(false);

  const { limits } = useTeamSetupAreasLimitContextRequired();

  const openForm = () => popupFormManager.openForm(<CreateTeamSetupAreaForm withInformationMessage={!!props.openFormOnLoad} />);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowButton(true);
    });

    if (props.openFormOnLoad) {
      openForm();
    }

    return () => clearTimeout(timeoutId);
  }, []);

  if (element && showButton) {
    return createPortal(
      (
        <Button
          text="Add Market"
          onClick={openForm}
          color={ButtonColor.BLUE}
          variant={ButtonVariant.FILLED}
          isDisabled={limits.remaining <= 0}
          title={limits.remaining <= 0 ? 'You have reached the limit of markets for your plan' : ''}
          iconType={IconType.PLUS}
        />
      ),
      element
    );
  } else {
    return null;
  }
}
